import { useI18n } from 'vue-i18n'

/**
 * Locale-aware relative-time formatter for chat surfaces (e.g. "last active
 * 3 min ago" / "vor 3 Min."). Uses the platform `Intl.RelativeTimeFormat`
 * bound to the active i18n locale, so en/de (and any future locale) format
 * correctly without a per-locale string table.
 *
 * Servers return RAW ISO timestamps; formatting happens here on the client so
 * it stays correct for the viewer's locale and updates if the locale changes.
 */
export function useTimeAgo() {
    const { locale } = useI18n()

    function format(input: string | number | Date | null | undefined): string {
        if (!input) return ''
        const then = input instanceof Date ? input.getTime() : new Date(input).getTime()
        if (Number.isNaN(then)) return ''

        const diffSec = Math.round((then - Date.now()) / 1000) // negative = past
        const abs = Math.abs(diffSec)

        let rtf: Intl.RelativeTimeFormat
        try {
            rtf = new Intl.RelativeTimeFormat(locale.value || 'en', { numeric: 'auto' })
        } catch {
            rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' })
        }

        if (abs < 45) return rtf.format(0, 'second') // "now" / "jetzt"
        if (abs < 3600) return rtf.format(Math.round(diffSec / 60), 'minute')
        if (abs < 86400) return rtf.format(Math.round(diffSec / 3600), 'hour')
        if (abs < 2592000) return rtf.format(Math.round(diffSec / 86400), 'day')
        if (abs < 31536000) return rtf.format(Math.round(diffSec / 2592000), 'month')
        return rtf.format(Math.round(diffSec / 31536000), 'year')
    }

    return { format }
}
